import React from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { 
  CreditCard, 
  TrendingUp, 
  Percent, 
  Gift, 
  Users, 
  CheckCircle, 
  Phone,
  Star,
  GraduationCap,
  Shield,
  Heart,
  MapPin,
  Award
} from 'lucide-react';
import { advisorInfo } from '../data/mock';

const BenefitsSection = () => {
  const loanFacilities = [
    {
      icon: <CreditCard className="w-6 h-6" />,
      title: "Full Loan (Tanpa Deposit)",
      description: "Sesuai untuk yang baru bekerja atau tak cukup simpanan"
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: "Fast Approval",
      description: "Close dengan banker, kelulusan lebih cepat & mudah"
    },
    {
      icon: <Percent className="w-6 h-6" />,
      title: "Lowest Interest Rate",
      description: "Kadar faedah terendah mengikut bank panel"
    },
    {
      icon: <CheckCircle className="w-6 h-6" />,
      title: "10% Deposit Option",
      description: "Sesuai untuk kurangkan ansuran bulanan"
    },
    {
      icon: <Award className="w-6 h-6" />,
      title: "Trade-In Kereta Lama",
      description: "Tolak terus dengan harga pasaran semasa"
    }
  ];

  const allGroups = [
    {
      icon: <GraduationCap className="w-6 h-6" />,
      title: "Student",
      description: "Boleh apply, wajib ada guarantor"
    },
    {
      icon: <CreditCard className="w-6 h-6" />,
      title: "PTPTN / Pinjaman Pendidikan",
      description: "Masih boleh lulus dengan bank tertentu"
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Tiada Lesen?",
      description: "Boleh apply jika ada ahli keluarga rujukan lesen"
    },
    {
      icon: <Star className="w-6 h-6" />,
      title: "Fresh Graduate",
      description: "Rebat khas RM1,000 untuk Axia & Myvi"
    },
    {
      icon: <CheckCircle className="w-6 h-6" />,
      title: "Non-license Holder",
      description: "Bank perlukan rujukan lesen ahli keluarga, bukan guarantor"
    },
    {
      icon: <Heart className="w-6 h-6" />,
      title: "Surirumah",
      description: "Boleh apply dengan guarantor (biasanya suami)"
    }
  ];

  const giftsRebates = [
    {
      icon: <Star className="w-6 h-6" />,
      title: "🚙 ARUZ",
      description: "Rebat Tunai RM8,000",
      highlight: true
    },
    {
      icon: <Star className="w-6 h-6" />,
      title: "🚗 ATIVA X / H",
      description: "Rebat Tunai RM6,500"
    },
    {
      icon: <Star className="w-6 h-6" />,
      title: "🚘 ATIVA AV",
      description: "Rebat Tunai RM7,000"
    },
    {
      icon: <GraduationCap className="w-6 h-6" />,
      title: "🎓 Fresh Graduate - AXIA",
      description: "Rebat Tambahan RM1,000"
    },
    {
      icon: <GraduationCap className="w-6 h-6" />,
      title: "🎓 Fresh Graduate - MYVI",
      description: "Rebat Tambahan RM1,000"
    },
    {
      icon: <GraduationCap className="w-6 h-6" />,
      title: "🎓 Fresh Graduate - ATIVA",
      description: "Rebat Tambahan RM500"
    },
    {
      icon: <Gift className="w-6 h-6" />,
      title: "Early Bird Gift",
      description: "Payung, tumbler, keychain, tinted voucher diskaun RM500"
    },
    {
      icon: <Heart className="w-6 h-6" />,
      title: "Customer Loyalty Reward",
      description: "Repeat buyer & trade-in dapat gift & rebat tambahan"
    }
  ];

  const afterSales = [
    {
      icon: <MapPin className="w-6 h-6" />,
      title: "Cover Seluruh Semenanjung Malaysia",
      description: "Tak kira negeri, semua boleh urus"
    },
    {
      icon: <Phone className="w-6 h-6" />,
      title: "Update Status Loan & Kereta",
      description: "Syasyah akan follow-up sampai kereta siap"
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "3S Centre (Service, Sales, Spare Part)",
      description: "Lokasi rasmi Perodua Lon G Setia Auto"
    },
    {
      icon: <Award className="w-6 h-6" />,
      title: "Jaminan Warranty Perodua",
      description: "Warranty 5 tahun / 150,000km"
    },
    {
      icon: <CheckCircle className="w-6 h-6" />,
      title: "Penerangan Clear",
      description: "Variant, harga, warna, installment → semua info lengkap sebelum submit loan"
    }
  ];

  return (
    <section id="benefits" className="py-20 bg-gradient-to-br from-pink-50 to-purple-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12 md:mb-16 px-4">
          <Badge className="bg-pink-100 text-pink-800 text-xs md:text-sm font-medium px-3 md:px-4 py-2 rounded-full mb-4">
            <Star className="w-3 h-3 md:w-4 md:h-4 mr-2" />
            Exclusive Benefits
          </Badge>
          <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-4 md:mb-6">
            Promosi & Kelebihan
            <span className="text-pink-600"> Beli Dengan Syasyah Perodua</span>
          </h2>
          <p className="text-base md:text-xl text-slate-600 max-w-3xl mx-auto">
            Dapatkan pengalaman terbaik dengan perkhidmatan eksklusif dan benefits istimewa
          </p>
        </div>

        {/* 1. Kemudahan Loan & Kewangan */}
        <div className="mb-16">
          <h3 className="text-xl md:text-2xl font-bold text-slate-900 mb-6 md:mb-8 text-center px-4">
            <span className="text-blue-600">🔹</span> Kemudahan Loan & Kewangan
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {loanFacilities.map((facility, index) => (
              <Card key={index} className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border-0 shadow-md bg-white rounded-xl">
                <CardContent className="p-4 md:p-6">
                  <div className="flex items-start space-x-3 md:space-x-4">
                    <div className="flex-shrink-0 w-10 h-10 md:w-12 md:h-12 bg-blue-50 rounded-lg flex items-center justify-center text-blue-600">
                      {facility.icon}
                    </div>
                    <div className="flex-1">
                      <h4 className="font-bold text-slate-900 mb-1 md:mb-2 text-xs md:text-sm">
                        {facility.title}
                      </h4>
                      <p className="text-slate-600 text-xs leading-relaxed">
                        {facility.description}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* 2. Khas Untuk Semua Golongan */}
        <div className="mb-16">
          <h3 className="text-xl md:text-2xl font-bold text-slate-900 mb-6 md:mb-8 text-center px-4">
            <span className="text-green-600">🔹</span> Khas Untuk Semua Golongan
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {allGroups.map((group, index) => (
              <Card key={index} className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border-0 shadow-md bg-white rounded-xl">
                <CardContent className="p-4 md:p-6">
                  <div className="flex items-start space-x-3 md:space-x-4">
                    <div className="flex-shrink-0 w-10 h-10 md:w-12 md:h-12 bg-green-50 rounded-lg flex items-center justify-center text-green-600">
                      {group.icon}
                    </div>
                    <div className="flex-1">
                      <h4 className="font-bold text-slate-900 mb-1 md:mb-2 text-xs md:text-sm">
                        {group.title}
                      </h4>
                      <p className="text-slate-600 text-xs leading-relaxed">
                        {group.description}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* 3. Gift & Rebate Eksklusif - November 2025 */}
        <div className="mb-16">
          <div className="text-center mb-8">
            <h3 className="text-xl md:text-2xl font-bold text-slate-900 mb-3 px-4">
              <span className="text-purple-600">🔹</span> ✨ PROMOSI PERODUA AKHIR TAHUN (NOV-DIS 2025) ✨
            </h3>
            <div className="inline-flex items-center space-x-2 bg-red-50 text-red-700 px-3 md:px-4 py-2 rounded-full text-xs md:text-sm font-semibold border-2 border-red-200 mx-4">
              <span>⏰</span>
              <span className="text-center">Year End Deals • Sah hingga Disember 2025</span>
            </div>
          </div>
          
          {/* Mobile: 1 column, Tablet: 2 columns, Desktop: 4 columns */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {giftsRebates.map((gift, index) => (
              <Card key={index} className={`group hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 border-0 shadow-lg rounded-xl ${
                gift.highlight 
                  ? 'bg-gradient-to-br from-orange-500 to-red-500 text-white' 
                  : 'bg-gradient-to-br from-purple-50 to-pink-50'
              }`}>
                <CardContent className="p-4 md:p-6">
                  <div className="text-center">
                    <div className={`w-12 h-12 md:w-16 md:h-16 rounded-xl flex items-center justify-center mx-auto mb-3 md:mb-4 ${
                      gift.highlight 
                        ? 'bg-white/20 text-white' 
                        : 'bg-gradient-to-br from-purple-100 to-pink-100 text-purple-600'
                    }`}>
                      {gift.icon}
                    </div>
                    <h4 className={`font-bold mb-2 text-xs md:text-sm ${
                      gift.highlight ? 'text-white' : 'text-slate-900'
                    }`}>
                      {gift.title}
                    </h4>
                    <p className={`text-xs leading-relaxed font-semibold ${
                      gift.highlight ? 'text-white' : 'text-slate-600'
                    }`}>
                      {gift.description}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* 4. Servis After-Sales & Extra Care */}
        <div className="mb-16">
          <h3 className="text-xl md:text-2xl font-bold text-slate-900 mb-6 md:mb-8 text-center px-4">
            <span className="text-orange-600">🔹</span> Servis After-Sales & Extra Care
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {afterSales.map((service, index) => (
              <Card key={index} className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border-0 shadow-md bg-white rounded-xl">
                <CardContent className="p-4 md:p-6">
                  <div className="flex items-start space-x-3 md:space-x-4">
                    <div className="flex-shrink-0 w-10 h-10 md:w-12 md:h-12 bg-orange-50 rounded-lg flex items-center justify-center text-orange-600">
                      {service.icon}
                    </div>
                    <div className="flex-1">
                      <h4 className="font-bold text-slate-900 mb-1 md:mb-2 text-xs md:text-sm">
                        {service.title}
                      </h4>
                      <p className="text-slate-600 text-xs leading-relaxed">
                        {service.description}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Final CTA */}
        <div className="text-center px-4">
          <Card className="bg-gradient-to-r from-pink-600 to-purple-600 text-white shadow-2xl rounded-2xl border-0 p-4 md:p-8">
            <CardContent className="p-0">
              <div className="max-w-3xl mx-auto">
                <div className="text-3xl md:text-5xl mb-4 md:mb-6">🎉</div>
                <h3 className="text-xl md:text-3xl font-bold mb-3 md:mb-4">
                  Semua Kelebihan Ini Untuk Anda!
                </h3>
                <p className="text-pink-100 mb-6 md:mb-8 text-sm md:text-lg">
                  Join 500+ customers yang telah merasai pengalaman terbaik beli kereta dengan Syasyah Perodua
                </p>
                
                <Button 
                  onClick={() => window.open(`${advisorInfo.whatsappLink}?text=${encodeURIComponent('[Website] PM PERODUA - Hi Syasyah, saya berminat dengan semua kelebihan yang anda offer. Boleh start proses loan sekarang?')}`, '_blank')}
                  className="bg-white text-pink-600 hover:bg-pink-50 font-bold px-6 md:px-10 py-3 md:py-4 rounded-xl text-base md:text-xl transition-all duration-300 hover:scale-105 shadow-xl w-full sm:w-auto"
                >
                  <Phone className="w-4 h-4 md:w-6 md:h-6 mr-2 md:mr-3" />
                  Mula Proses Loan Sekarang
                </Button>

                {/* Trust Indicators */}
                <div className="flex flex-wrap items-center justify-center gap-4 md:gap-8 mt-6 md:mt-8 text-pink-100">
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 md:w-5 md:h-5" />
                    <span className="font-medium text-xs md:text-base">98% Approval</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Users className="w-4 h-4 md:w-5 md:h-5" />
                    <span className="font-medium text-xs md:text-base">500+ Customers</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Star className="w-4 h-4 md:w-5 md:h-5 fill-current" />
                    <span className="font-medium text-xs md:text-base">5.0 Rating</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default BenefitsSection;